<template>
  <div :class="{ 'taro-header--sticky': sticky }">
    <!--
      自訂導覽的手機抽屜。放在 app-bar 外面，否則會被 toolbar 的 overflow 裁掉。

      有子項的主項預設收合，按了才展開（2026-10 老闆要求，原本是三層全部攤平）。

      收合會撞到一個矛盾：主項本身也可以是一個頁面，點它要轉跳並關掉抽屜，
      那「展開」要按哪裡？做法是把一列拆成兩個按的地方 ——

        文字   有連結就轉跳（抽屜跟著關）；沒有連結就展開／收合
        箭頭   只負責展開／收合，44px 見方，手指好按

      目前所在的頁面如果在某一組底下，打開抽屜時那一組會自動展開，
      使用者看得到自己在哪裡，不用自己找。

      「哪一項屬於哪一項」靠視覺表達，所以分兩種角色：

        主項  15px / 600 / 黑
        子項  13px / 400 / 灰，整組共用左邊一條導引線

      **沒有連結的主項也是主項。** 它跟其他第一層是同一階，所以用同一個
      v-list-item 渲染、同樣的縮排字級顏色，差別只有點下去不會轉跳。

      之前它是另外一個 <div class="taro-drawer__group-label">，縮排比
      v-list-item 多 8px、字色又調淡，於是看起來像「比別人低一級的東西」——
      使用者實際回報看不出層級關係。層級只有一個來源：縮排。
      同一層就對齊，不靠顏色深淺去暗示階層。
    -->
    <v-navigation-drawer v-if="navMenu" v-model="navDrawer" fixed temporary left width="272" style="z-index: 1000">
      <v-list nav dense class="taro-drawer py-3">
        <template v-for="item in navItems">
          <!--
            能點的母項與不能點的母項走同一個 v-list-item —— 縮排、字級、
            字重、顏色全部由同一條規則給，不可能再對不齊。

            不能點的那種就是「不 v-bind 連結屬性」而已：VListItem 要有
            to / href / click 監聽才算 clickable，都沒有時它自己就不會有
            hover 與漣漪，點下去也不會轉跳 —— 正是要的行為。

            不能走 navLinkProps —— 它對沒有 href 的項目回 { disabled: true }，
            Vuetify 直接壓到 38% 透明度，看起來像壞掉的選項而不是標題。
          -->
          <div :key="'drawer-' + item.key" class="taro-drawer__row">
            <!--
              沒有連結的主項：整列都是展開／收合（右邊的箭頭只是提示）。
              有連結的主項：這一列照常轉跳，展開交給旁邊獨立的箭頭按鈕。
              箭頭不放進 v-list-item 裡面 —— 那是一個 <a>，按鈕包在連結裡不但是
              不合法的 HTML，點箭頭時還得跟連結的轉跳搶事件。
            -->
            <v-list-item
              v-bind="isNavGroupOnly(item) ? {} : navLinkProps(item)"
              v-on="isNavGroupOnly(item) ? { click: () => toggleDrawerGroup(item) } : {}"
              class="taro-drawer__parent"
              :class="{
                'taro-drawer__parent--active': isNavItemActive(item),
                'taro-drawer__parent--group': isNavGroupOnly(item),
              }"
              :aria-expanded="isNavGroupOnly(item) ? String(isDrawerGroupOpen(item)) : undefined"
            >
              <v-list-item-content>
                <v-list-item-title class="taro-drawer__parent-text">{{ item.label }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action v-if="isNavGroupOnly(item)" class="my-0 taro-drawer__group-chevron">
                <v-icon class="taro-drawer__chevron" :class="{ 'taro-drawer__chevron--open': isDrawerGroupOpen(item) }">
                  mdi-chevron-down
                </v-icon>
              </v-list-item-action>
            </v-list-item>

            <v-btn
              v-if="!isNavGroupOnly(item) && hasNavChildren(item)"
              icon
              class="taro-drawer__toggle"
              :aria-label="(isDrawerGroupOpen(item) ? '收合 ' : '展開 ') + item.label"
              :aria-expanded="String(isDrawerGroupOpen(item))"
              @click.stop="toggleDrawerGroup(item)"
            >
              <v-icon class="taro-drawer__chevron" :class="{ 'taro-drawer__chevron--open': isDrawerGroupOpen(item) }">
                mdi-chevron-down
              </v-icon>
            </v-btn>
          </div>

          <!-- 子項整組包起來，導引線才是一條連續的，而不是每列各畫一小段 -->
          <v-expand-transition v-if="hasNavChildren(item)" :key="'drawer-sub-' + item.key">
          <div v-show="isDrawerGroupOpen(item)" class="taro-drawer__children">
            <template v-for="child in item.children">
              <v-list-item :key="'drawer-c-' + child.key" v-bind="navLinkProps(child)" class="taro-drawer__child">
                <v-list-item-content>
                  <v-list-item-title class="taro-drawer__child-text">{{ child.label }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>

              <v-list-item
                v-for="grandChild in child.children || []"
                :key="'drawer-gc-' + grandChild.key"
                v-bind="navLinkProps(grandChild)"
                class="taro-drawer__child taro-drawer__child--deep"
              >
                <v-list-item-content>
                  <v-list-item-title class="taro-drawer__child-text">{{ grandChild.label }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
          </div>
          </v-expand-transition>
        </template>
      </v-list>
    </v-navigation-drawer>

    <!--
      有自訂導覽時比照 yentity 的規格（手機 52 / 桌機 68，見 appBarHeight）。
      沒有 navMenu 的既有頁面維持原本固定 64，不動到它們的版面。
    -->
    <v-app-bar color="white" :height="appBarHeight" :class="{ 'taro-nav__bar': navMenu }" style="z-index: 100; box-shadow: 0px 1px 4px rgba(117, 125, 132, 0)">
      <!--
        #region 自訂導覽（子站專用）
        傳入 navMenu 時，左側與中間換成該子站的 LOGO 與導覽列；
        右側的服務菜單 / 通知 / 頭像完全不動，所以登入狀態照舊會反映。
        沒傳就完全維持原本 version 1 / 2 的行為。
      -->
      <template v-if="navMenu">
        <!--
          手機：漢堡開左側抽屜（樣式比照 yentity；項目多時下拉不好用）

          用 d-md-none 而不是 hidden-md-and-up —— 兩者效果相同，
          但與下面的 d-none d-md-flex 同一套命名，改的時候不會漏。
        -->
        <v-btn icon class="d-md-none ml-n2" @click.stop="navDrawer = !navDrawer">
          <v-icon color="black">mdi-menu</v-icon>
        </v-btn>

        <a :href="navLogoLink" class="d-flex align-center flex-shrink-0">
          <img :src="navLogo" class="taro-nav__logo" />
        </a>

        <!--
          桌機：整排展開。

          **不要寫成 `hidden-sm-and-down d-flex`** —— hidden-* 是
          display:none !important，d-flex 是 display:flex !important，
          兩個都 !important 時勝負由 CSS 檔案的先後順序決定（d-flex 在後面會贏），
          結果是手機上導覽和漢堡同時出現。
          正確寫法是同一套響應式工具類：預設 none，md 以上才 flex。
        -->
        <div class="d-none d-md-flex align-center taro-nav ml-4">
          <template v-for="item in navItems">
            <!--
              母項自己也要能點進去。

              關鍵是 :open-on-click="false"：Vuetify 的 activator 預設會綁一個
              click 去開選單，那顆 click 會把導覽用掉，母項就只剩下拉功能。
              關掉之後 `on` 只剩 hover 事件，click 才會落到 router-link 上。

              代價是純觸控裝置上沒有 hover。桌機以下走側邊抽屜（d-md-none），
              抽屜裡母項和子項都是獨立的連結，所以影響只到「觸控筆電」這種
              少數情況 —— 那裡點母項會直接進母項頁面，不會卡住。
            -->
            <header-menu
              v-if="item.children && item.children.length"
              :key="'nav-m-' + item.key"
              offset-y
              open-on-hover
              :open-on-click="false"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  text
                  plain
                  class="taro-nav__item"
                  :class="{ 'taro-nav__item--active': isNavItemActive(item) }"
                  v-bind="{ ...attrs, ...navParentProps(item) }"
                  v-on="on"
                >
                  {{ item.label }}
                  <v-icon size="16" right>mdi-chevron-down</v-icon>
                </v-btn>
              </template>
              <!--
                第三層直接攤在同一個面板裡縮排，不做飛出式子選單。

                飛出式試過，會按不到：子選單用 offset-x 開在旁邊，中間那幾 px
                的間隙是死區，滑鼠一離開母項就把兩層都關掉 —— 而且是 hover 才有的
                問題，點擊測不出來。要修得靠 close-delay 加上把間隙補滿，
                但那是在跟兩層 overlay 的 hover 狀態搏鬥，導覽項目本來也不多。
                攤平就沒有這個問題，一眼也看得到全部。
              -->
              <v-list dense>
                <template v-for="child in item.children">
                  <v-list-item :key="child.key" v-bind="navLinkProps(child)">
                    <v-list-item-title class="taro-nav__text">{{ child.label }}</v-list-item-title>
                  </v-list-item>

                  <v-list-item
                    v-for="grandChild in child.children || []"
                    :key="grandChild.key"
                    v-bind="navLinkProps(grandChild)"
                    class="taro-nav__sub"
                  >
                    <v-list-item-title class="taro-nav__text taro-nav__text--sub">
                      {{ grandChild.label }}
                    </v-list-item-title>
                  </v-list-item>
                </template>
              </v-list>
            </header-menu>

            <v-btn v-else :key="'nav-b-' + item.key" text plain class="taro-nav__item" v-bind="navLinkProps(item)">
              {{ item.label }}
            </v-btn>
          </template>
        </div>

        <v-spacer />
      </template>
      <!-- #endregion -->

      <div v-if="!navMenu && version === 1" class="d-flex align-center">
        <a :href="url" class="d-flex align-center">
          <img :src="logo" style="height: 32px; width: 108px" />
        </a>
      </div>

      <!-- 手機版只留圖示，標題才有空間 -->
      <v-btn
        v-if="!navMenu && version === 2"
        @click="prePage"
        :class="$vuetify.breakpoint.xsOnly ? 'px-0' : 'px-2'"
        :min-width="$vuetify.breakpoint.xsOnly ? 40 : 64"
        :aria-label="hasHistory ? '返回' : '首頁'"
      >
        <v-icon size="18" :left="!$vuetify.breakpoint.xsOnly">{{ hasHistory ? "mdi-arrow-left" : "mdi-home" }}</v-icon>
        <span v-if="!$vuetify.breakpoint.xsOnly">{{ hasHistory ? "返回" : "首頁" }}</span>
      </v-btn>
      <v-spacer v-if="!navMenu" />
      <div v-if="!navMenu && version === 2" class="text-h6 text-sm-h5 font-weight-medium text-truncate ml-2" style="max-width: 300px">{{ pageTitle }}</div>
      <v-spacer v-if="!navMenu" />
      <!-- #region 各式服務菜單 -->
      <!--
        對外品牌站不放會員區（服務菜單、通知、會員選單、登入/註冊）。

        子站在後台勾了「隱藏小芋頭登入與服務選單」才會進這個模式，
        子站網域與平台網域一致。判斷邏輯見 publicSiteMode。
      -->
      <template v-if="!publicSiteMode">
      <!--
        寬 400，與社群站、AI 站一致。原本 366，清單改成在選單內捲動後，
        捲軸吃掉 6px，「免費金豐勞動」這種六個字的名稱就被擠成兩行。
        手機上的寬度上限見 .taro-service-card。
      -->
      <header-menu offset-y nudge-bottom="12" :nudge-width="400" max-width="400" transition="slide-y-transition">
        <template v-slot:activator="{ attrs, on }">
          <v-btn fab v-bind="attrs" v-on="on" text x-small>
            <v-icon size="24">mdi-apps</v-icon>
          </v-btn>
        </template>
        <v-card class="taro-service-card">
          <div class="font-weight-medium py-4 px-6">小芋頭服務</div>
          <div class="taro-service-scroll">
            <!-- 首次載入(沒有快取)才鋪骨架屏，先把版面高度佔住，避免清單進來時整塊跳動 -->
            <!-- 手機（< 600px）選單只剩畫面寬減 24px，左右留白縮小，名稱才排得下 -->
            <v-row v-if="serviceLoading" class="d-flex mx-2 mx-sm-4">
              <v-col cols="4" v-for="n in serviceSkeletonCount" :key="'service-skeleton-' + n" class="pa-1">
                <div class="justify-center text-center py-2 px-1 px-sm-2">
                  <div class="d-flex justify-center">
                    <div class="service-skeleton service-skeleton-icon rounded-lg"></div>
                  </div>
                  <div class="service-skeleton service-skeleton-text mt-2 mx-auto"></div>
                  <div class="service-skeleton service-skeleton-text mt-1 mx-auto" style="width: 60%"></div>
                </div>
              </v-col>
            </v-row>
            <v-row v-else class="d-flex mx-2 mx-sm-4">
              <v-col cols="4" v-for="x in taroService" :key="x.name" class="pa-1">
                <div class="justify-center text-center service-block py-2 px-1 px-sm-2" @click="GoTaroService(x)">
                  <div class="d-flex justify-center">
                    <!-- height/width 寫死 38：圖還沒下載完也先佔住位置，placeholder 放骨架 -->
                    <v-img :src="x.icon" contain eager height="38" width="38" max-height="38" max-width="38" class="rounded-lg">
                      <template #placeholder>
                        <div class="service-skeleton service-skeleton-icon rounded-lg"></div>
                      </template>
                    </v-img>
                  </div>
                  <div class="mt-1 text-body-2" v-html="x.name"></div>
                </div>
              </v-col>
            </v-row>
          </div>
          <div class="w-100 px-6 pt-2 pb-4">
            <v-divider class="mb-2"></v-divider>
            <v-btn @click="showShare = true" text block large height="48" class="justify-start px-2" style="font-size: 16px">
              <v-icon size="20" class="mr-4">mdi-share-variant</v-icon>
              分享小芋頭平台
            </v-btn>
          </div>
          <!-- <v-divider class="my-2"></v-divider>
          <div class="d-flex justify-space-around">
            <v-btn @click="showShareOrg = true" text>
              <v-icon left>mdi-share-variant</v-icon>
              分享小芋頭平台
            </v-btn>
            <v-btn @click="Share('/ads')" text>
              <v-icon left>mdi-share-variant</v-icon>
              分享小芋頭廣告
            </v-btn>
          </div> -->
        </v-card>
      </header-menu>
      <!-- #endregion -->

      <!-- #region 通知鈴鐺 -->
      <header-menu v-if="$store.state.signinStatus && enableNotifications" v-model="notifyMenu" offset-y nudge-bottom="12" :nudge-width="360" max-width="380" :close-on-content-click="false" transition="slide-y-transition" left>
        <template v-slot:activator="{ attrs, on }">
          <v-btn fab v-bind="attrs" v-on="on" text x-small class="ml-1" @click="onOpenNotify">
            <v-badge :content="notifyUnread" :value="notifyUnread > 0" color="red" overlap>
              <v-icon size="24">mdi-bell-outline</v-icon>
            </v-badge>
          </v-btn>
        </template>
        <v-card max-width="380" class="notify-panel">
          <!-- 頭尾兩條 bar 等高，見 .notify-panel 的 --notify-bar-height -->
          <div class="d-flex align-center justify-space-between px-4 notify-panel__bar">
            <span class="font-weight-medium">通知</span>
            <!-- 原本是 x-small（10px），讀不到。文字按鈕不要用 x-small -->
            <v-btn text small color="primary" :disabled="notifyUnread === 0" @click="onMarkAllRead">全部已讀</v-btn>
          </div>
          <v-divider></v-divider>
          <div style="max-height: 380px; overflow-y: auto">
            <template v-if="notifyItems.length">
              <div v-for="n in notifyItems" :key="n.id" class="notify-item px-4 py-3" :class="{ 'notify-unread': !n.isRead }" @click="onClickNotify(n)" style="cursor: pointer">
                <div class="d-flex align-start">
                  <div class="notify-item__main">
                    <div class="d-flex align-center">
                      <!-- 來源站台的主圖示 -->
                      <img
                        :src="sourceIcon(n.sourceSite)"
                        :alt="sourceName(n.sourceSite)"
                        :title="sourceName(n.sourceSite)"
                        class="notify-source-icon mr-2"
                      />
                      <span class="text-body-2 font-weight-medium text-truncate">{{ n.title }}</span>
                      <v-spacer></v-spacer>
                      <span v-if="!n.isRead" class="notify-dot ml-2"></span>
                    </div>
                    <div class="text-caption text--secondary mt-1" style="white-space: normal">{{ n.body }}</div>
                    <div class="text-caption grey--text mt-1">{{ formatNotifyTime(n.createdAt) }}</div>
                  </div>
                  <!-- 後台發送時附的自訂圖片；載不到就整個拿掉，不留破圖 -->
                  <img
                    v-if="n.imageUrl && !failedNotifyImages[n.imageUrl]"
                    :src="n.imageUrl"
                    alt=""
                    class="notify-item__image ml-3"
                    loading="lazy"
                    @error="$set(failedNotifyImages, n.imageUrl, true)"
                  />
                </div>
                <v-divider class="mt-3"></v-divider>
              </div>
            </template>
            <div v-else class="text-center text--secondary py-8 text-body-2">目前沒有通知</div>
          </div>
          <v-divider></v-divider>
          <v-btn text block color="primary" class="notify-panel__bar-action" @click="goNotificationCenter">查看全部</v-btn>
        </v-card>
      </header-menu>
      <!-- #endregion -->

      <!-- #region 右邊頭像菜單 -->
      <template v-if="$store.state.signinStatus">
        <header-menu v-model="menu" offset-y nudge-bottom="8" :nudge-width="375" max-width="375" :close-on-content-click="true" class="white" transition="slide-y-transition" left>
          <template v-slot:activator="{ on, attrs }">
            <v-avatar class="ml-1" color="blue darken-1" size="36" v-bind="attrs" v-on="on">
              <v-img v-if="$store.state.userPhoto" :src="$store.state.userPhoto" cover></v-img>
              <div v-else class="white--text">
                {{ avatarText }}
              </div>
            </v-avatar>
          </template>
          <v-card class="rounded-lg" style="overflow-y: auto; overflow-x: hidden; max-height: 85vh">
            <div class="info-menu-bg pa-4">
              <div class="white rounded-lg w-100">
                <div class="d-flex align-center pa-2 pt-4">
                  <v-avatar class="ml-1 mr-2 avatar-border" color="blue darken-1" size="48">
                    <v-img v-if="$store.state.userPhoto" :src="$store.state.userPhoto" cover></v-img>
                    <div v-else class="white--text">
                      {{ avatarText }}
                    </div>
                  </v-avatar>
                  <div class="d-flex align-center" style="min-width: 0; overflow: hidden">
                    <div class="text-truncate font-weight-medium mr-2" style="max-width: 120px; flex-shrink: 1">
                      {{ $store.state.userRealName }}
                    </div>
                    <div v-if="userGradeName" class="grade-badge linear-gradient-color text-body-2 white--text px-2 py-1 rounded-pill" style="white-space: nowrap; flex-shrink: 0">{{ userGradeName }}</div>
                  </div>
                </div>
                <div class="d-flex justify-left px-2">
                  <v-btn text height="auto" class="pa-2 menu-icon-btn" @click="goCenter('order')">
                    <div class="d-flex flex-column align-center">
                      <v-icon class="mb-1">mdi-invoice-text</v-icon>
                      <div class="text">商品訂單</div>
                    </div>
                  </v-btn>
                  <v-btn text height="auto" class="pa-2 menu-icon-btn" @click="goCenter('subscription')">
                    <div class="d-flex flex-column align-center">
                      <v-icon class="mb-1">mdi-briefcase</v-icon>
                      <div class="text">我的訂閱</div>
                    </div>
                  </v-btn>
                  <v-btn v-if="userGradeName" text height="auto" class="pa-2 menu-icon-btn" @click="goCenter('agent')">
                    <div class="d-flex flex-column align-center">
                      <v-icon class="mb-1">mdi-account-group</v-icon>
                      <div class="text">組織訂單</div>
                    </div>
                  </v-btn>
                  <v-btn v-if="userGradeName" text height="auto" class="pa-2 menu-icon-btn" @click="goCenter('chicken')">
                    <div class="d-flex flex-column align-center">
                      <v-icon class="mb-1">mdi-currency-usd</v-icon>
                      <div class="text">雞肉飼料</div>
                    </div>
                  </v-btn>
                </div>
                <div class="my-1 pa-2">
                  <v-btn text height="40" class="justify-start" block @click="GoRouter('Center')">
                    <v-icon left size="20" class="mr-3">mdi-cog</v-icon>
                    <div>個人會員中心</div>
                  </v-btn>
                  <!-- 修改密碼、登入中的裝置：會員中心的「帳號安全」分頁 -->
                  <v-btn text height="40" class="justify-start mt-1" block @click="goCenter('security')">
                    <v-icon left size="20" class="mr-3">mdi-shield-account</v-icon>
                    <span class="text-truncate">帳號安全</span>
                  </v-btn>

                  <div class="d-flex justify-space-between align-center" style="flex-wrap: wrap; gap: 4px">
                    <v-btn
                      text
                      height="40"
                      class="d-flex justify-start"
                      style="flex: 1 1 auto; min-width: 0"
                      @click="
                        menu = false;
                        showShare = true;
                      "
                    >
                      <v-icon left size="20" class="mr-3">mdi-share-variant</v-icon>
                      <span class="text-truncate">會員邀請分享</span>
                    </v-btn>

                    <v-btn min-width="72" height="32" @click="showShareOrg = true" style="flex-shrink: 0">分享紀錄</v-btn>
                  </div>

                  <v-btn text height="40" class="justify-start mt-1" block @click="GoRouter('Upgrade')">
                    <v-icon left size="20" class="mr-3">mdi-account-group</v-icon>
                    <span class="text-truncate">加入終身俸計劃</span>
                  </v-btn>

                  <!-- :disabled="!isTestEnv" -->
                  <v-btn text height="40" class="justify-start mt-1" block @click="GoRouter('Product')">
                    <v-icon left size="20" class="mr-3">mdi-shopping</v-icon>
                    <span>小芋頭商品中心</span>
                  </v-btn>
                </div>
              </div>

              <div v-if="showAiPreorderMenu" class="pa-3 white rounded-lg w-100">
                <div class="my-1">
                  <div class="font-weight-medium mb-4 cyan--text text--darken-3">企業AI會員預購</div>

                  <v-btn block variant="text" append-icon="" class="d-flex text-white pa-2 mb-1 linear-gradient-color rounded-lg justify-space-between align-center" height="44" @click="goPreOrder(1)">
                    <div class="d-flex align-center w-100" style="min-width: 0; overflow: hidden">
                      <img src="@/assets/image/common/lv_silver.png" alt="lv_silver.png" class="mr-2" style="flex-shrink: 0" />
                      <div class="white--text text-truncate">提升企業AI行銷會員</div>
                      <v-spacer></v-spacer>
                      <v-icon right color="white" style="flex-shrink: 0">mdi-chevron-right</v-icon>
                    </div>
                  </v-btn>

                  <v-btn block variant="text" append-icon="mdi-chevron-right" class="d-flex text-white pa-2 linear-gradient-color rounded-lg justify-space-between align-center" height="44" @click="goPreOrder(2)">
                    <div class="d-flex align-center w-100" style="min-width: 0; overflow: hidden">
                      <img src="@/assets/image/common/lv_gold.png" alt="lv_gold.png" class="mr-2" style="flex-shrink: 0" />
                      <div class="white--text text-truncate">提升企業AI智庫會員</div>
                      <v-spacer></v-spacer>
                      <v-icon right color="white" style="flex-shrink: 0">mdi-chevron-right</v-icon>
                    </div>
                  </v-btn>
                </div>
              </div>

              <div class="pa-1 white rounded-lg w-100">
                <v-btn block @click="GoRouter('Home')" text height="40" class="justify-start">
                  <v-icon left size="22" class="mr-3">mdi-home</v-icon>
                  <span>回首頁</span>
                </v-btn>
                <!-- 切換帳號（components/Common/AccountSwitchDialog.vue）。子站自訂網域不做：登入在平台網域 -->
                <v-btn v-if="canSwitchAccount" block @click="$store.commit('SetAccountSwitcher', true)" text height="40" class="justify-start mt-1">
                  <v-icon left size="20" class="mr-3">mdi-account-switch-outline</v-icon>
                  <span>切換帳號</span>
                </v-btn>
                <v-btn block @click="logout" text height="40" class="justify-start mt-1">
                  <v-icon left size="20" class="mr-3">mdi-logout</v-icon>
                  <span>登出</span>
                </v-btn>
              </div>
            </div>
          </v-card>
        </header-menu>
      </template>
      <template v-else>
        <v-btn class="ml-2" color="primary" outlined @click="GoRouter('SignIn')">登入</v-btn>
        <v-btn class="ml-2" color="primary" @click="GoRouter('SignUp')">註冊</v-btn>
      </template>
      </template>

      <!-- #endregion -->

      <!--  #region 提示條 -->
      <v-snackbar top class="position: sticky; position: -webkit-sticky; bottom: 0;" v-model="snackbar.show" :timeout="1500">
        {{ snackbar.content }}
        <template v-slot:action="{ attrs }">
          <v-btn color="white" text v-bind="attrs" @click="snackbar.show = false">
            {{ snackbar.close }}
          </v-btn>
        </template>
      </v-snackbar>
      <!-- #endregion -->
    </v-app-bar>

    <!-- <v-divider></v-divider> -->
    <!-- 分享 dialog -->
    <ShareDialog v-model="showShare" />
    <!-- 分享組織圖 dialog -->
    <ShareOrgDialog v-model="showShareOrg" />
  </div>
</template>

<script>
// import QuickMenu from "./QuickMenu";
import { openLoginDialog } from "@/utils/loginDialog";
import { Grade_GetUserGradeName } from "@/apis/api";
import { ENABLE_NOTIFICATIONS } from "@/config/features";
import { isCustomDomain } from "@/utils/custom-domain";
import { PLATFORM_BASE, navigateMemberArea, platformSignInUrl } from "@/utils/custom-domain-auth";
import { signOutAndReload } from "@/utils/signOut";
import { sourceIcon, sourceName } from "@/utils/notification-source";
import { leavesPage, waitAtMost, resolveNotificationRoute } from "@/utils/notification-nav";

import ShareDialog from "./ShareDialog.vue";
import ShareOrgDialog from "./ShareOrgDialog.vue";
// header 包在 sticky 的 .fixed-bar 裡，v-menu 要換成認得 sticky 的版本，原因見檔內說明
import HeaderMenu from "./HeaderMenu";
// 別名：元件方法也叫 loadTaroServices，不改名的話讀起來像在遞迴
import { loadTaroServices as fetchTaroServices } from "@/utils/taro-services";
import { leaveTo } from "@/utils/leavingOverlay";

// 服務清單快取：Google Sheet CSV 每次都重抓要 1~2 秒，先吃 localStorage 秒開、背景再更新。

export default {
  components: {
    // QuickMenu,
    HeaderMenu,
    ShareDialog,
    ShareOrgDialog,
  },
  props: {
    version: {
      type: Number,
      required: false,
      default: 2,
    },
    /**
     * 自訂導覽選單（子站用）。結構同公開 API `by-slug` 回傳的 navMenu：
     * { logoImageUrl, logoLinkUrl, items: [{ id, label, href, openInNewTab, children }] }
     *
     * href 由後端解析完成（page 型別已轉成 /p/{slug}），前端不需要再查一次。
     * 目標頁被下架時後端會回 null，這裡就把該項目顯示成不可點。
     */
    navMenu: {
      type: Object,
      required: false,
      default: null,
    },
    pageTitle: {
      type: String,
      required: false,
      default: "金豐小芋頭平台創業",
    },
    backRouter: {
      type: [String, Object],
      required: false,
      default: null,
    },
    /**
     * 只讓 app bar 本身黏在頂端（子站用）。原因見 .taro-header--sticky。
     * 平台其他頁是在外面包 .fixed-bar，那些頁面沒有導覽抽屜，不受影響。
     */
    sticky: {
      type: Boolean,
      default: false,
    },
  },
  name: "Header",
  data() {
    return {
      // 早鳥預購已於 2026-10-07 結束，「企業AI會員預購」選單先隱藏；要恢復改成 true
      showAiPreorderMenu: false,
      // 頭像選單的「切換帳號」：子站自訂網域沒有（登入在平台網域）
      canSwitchAccount: !isCustomDomain(),
      showShare: false,
      showShareOrg: false,
      notifyMenu: false,
      // 載入失敗的通知圖片網址；記在元件裡，不去改 store 裡的通知物件
      failedNotifyImages: {},
      hasHistory: true, // 新增：檢測是否有上一頁
      userGradeName: null,
      snackbar: {
        show: false,
        content: "",
        close: "關閉",
      },
      logo: require("@/assets/tarologo.png"),
      title: "小芋頭",
      index: "首頁",
      url: process.env.VUE_APP_MAIN_BASE_URL,
      menu: false,
      userInfo: {
        photo: require("@/assets/image/common/user-img.svg"),
        email: null,
        name: null,
      },
      btnText: "登入",
      // 自訂導覽的手機抽屜開關
      navDrawer: false,
      // 抽屜裡各組的展開狀態，key 是主項的 key；沒有值表示使用者還沒動過
      drawerOpen: {},
      // 首次載入(無快取)時顯示骨架屏；有快取就直接出清單，不閃骨架
      serviceLoading: false,
      serviceSkeletonCount: 9,
      taroService: [
        {
          name: "小芋頭平台",
          icon: require("@/assets/image/serivceIcon/platform_icon.svg"),
          action: process.env.VUE_APP_MAIN_BASE_URL,
          target: "_self",
        },
        {
          name: "小芋頭社群",
          icon: require("@/assets/image/serivceIcon/sms_icon.svg"),
          action: process.env.VUE_APP_MAIN_SMS_BASE_URL,
          target: "_self",
        },
        {
          name: "小芋頭廣告",
          icon: require("@/assets/image/serivceIcon/ad_icon.svg"),
          action: process.env.VUE_APP_MAIN_BASE_URL + "/ads",
          target: "_self",
        },
        {
          name: "金豐勞動法務AI智能諮詢系統",
          icon: require("@/assets/image/ai/金豐ai公版icon.svg"),
          action: process.env.VUE_APP_MAIN_AICHAT_BASE_URL,
          target: "_self",
        },
        {
          name: "小芋頭商城",
          icon: require("@/assets/image/home/icon_shop.svg"),
          action: process.env.VUE_APP_MAIN_BASE_URL + "/product",
          target: "_self",
        },
        {
          name: "加入終身俸計劃",
          icon: require("@/assets/image/home/icon_upgrade.svg"),
          action: process.env.VUE_APP_MAIN_BASE_URL + "/upgrade",
          target: "_self",
        },
        {
          name: "金豐勞動<br>法務健檢網",
          icon: require("@/assets/image/serivceIcon/llh_icon.svg"),
          action: "https://labor.kf-9595.com/",
          target: "_blank",
        },
        {
          name: "免費法律諮詢",
          icon: require("@/assets/image/serivceIcon/legal_icon.svg"),
          action: process.env.VUE_APP_MAIN_BASE_URL + "/product/46/",
          target: "_self",
        },
        {
          name: "企業勞動<br>法務講座",
          icon: require("@/assets/image/serivceIcon/lecture_icon.svg"),
          action: process.env.VUE_APP_MAIN_BASE_URL + "/product/38/",
          target: "_self",
        },
        {
          name: "勞動法務<br>考證課程",
          icon: require("@/assets/image/serivceIcon/certificate_icon.svg"),
          action: process.env.VUE_APP_MAIN_BASE_URL + "/product/36/",
          target: "_self",
        },
        {
          name: "勞資爭議人員訓練班",
          icon: require("@/assets/image/serivceIcon/training_icon.svg"),
          action: process.env.VUE_APP_MAIN_BASE_URL + "/product/39/",
          target: "_self",
        },
        {
          name: "企業大學課程",
          icon: require("@/assets/image/serivceIcon/university_icon.svg"),
          action: "https://school.king-fong.com/",
          target: "_blank",
        },
        {
          name: "Yentity<br>時尚品牌",
          icon: require("@/assets/projectYentity/icon.png"),
          action: process.env.VUE_APP_MAIN_BASE_URL + "/yentity",
          target: "_self",
        },
        {
          name: "德利ISO服務",
          icon: require("@/assets/image/serivceIcon/iso_icon.svg"),
          action: process.env.VUE_APP_MAIN_BASE_URL + "/product/58/",
          target: "_self",
        },
        {
          name: "朝和生醫<br>健康食品",
          icon: require("@/assets/image/serivceIcon/jhaoho_icon.png"),
          action: process.env.VUE_APP_MAIN_BASE_URL + "/jhaoho",
          target: "_self",
        },
        {
          name: "金豐薪資<br>雲端網",
          icon: require("@/assets/image/serivceIcon/hr1_icon.svg"),
          action: "https://hr.king-fong.com/",
          target: "_blank",
        },
      ],
    };
  },
  mounted() {
    this.hasHistory = window.history.length > 1;
    this.userInfo.email = localStorage.getItem("_signinEmail");

    let emailCode = localStorage.getItem("_emailEncrypt");
    if (emailCode && emailCode !== "null" && emailCode !== "undefined") {
      this.getUserGradeName_api(emailCode);
    }

    this.syncFromQuery(this.$route.query.org);
    this.loadTaroServices();

    // 通知：登入時啟動未讀數輪詢(module 內已防重複計時器)
    if (ENABLE_NOTIFICATIONS && this.$store.state.signinStatus) {
      this.$store.dispatch("notifications/startPolling");
    }
  },

  watch: {
    /**
     * 打開抽屜時，目前所在頁面的那一組自動展開 —— 收合是為了版面清爽，
     * 不該讓使用者連自己在哪一頁都要點開找。使用者自己收起來過的組不再強開。
     */
    navDrawer(open) {
      if (!open) return;
      this.navItems.forEach((item) => {
        if (this.hasNavChildren(item) && this.isNavItemActive(item) && this.drawerOpen[item.key] === undefined) {
          this.$set(this.drawerOpen, item.key, true);
        }
      });
    },
  },

  computed: {
    /**
     * app bar 高度。只有自訂導覽（子站）才隨螢幕縮 ——
     * 既有的 10 幾個頁面維持原本固定 64，避免動到它們的版面。
     */
    appBarHeight() {
      if (!this.navMenu) return 64;
      // 與 YentityHeader 一致（桌機 68 / 手機 52）——
      // 子站導覽的視覺規格就是照 yentity 那一套，數字不要各走各的。
      return this.$vuetify.breakpoint.smAndDown ? 52 : 68;
    },
    /** 自訂選單的項目；沒有就回空陣列，template 才不用到處判斷 null */
    navItems() {
      return (this.navMenu && this.navMenu.items) || [];
    },
    /** 子站 LOGO；沒設就退回平台 LOGO，不要讓 header 左邊開天窗 */
    navLogo() {
      return (this.navMenu && this.navMenu.logoImageUrl) || this.logo;
    },
    navLogoLink() {
      return (this.navMenu && this.navMenu.logoLinkUrl) || this.url;
    },
    isTestEnv() {
      // 依據環境變數決定是否為測試環境
      return process.env.VUE_APP_PUBLIC_ENV !== "production";
    },
    /** 通知功能總開關（暫未上正式） */
    /**
     * 對外品牌站模式：不顯示小芋頭會員區。
     *
     * 平台自己的頁面沒有 navMenu、也不在自訂網域上，永遠是 false，
     * 所以既有的十幾個頁面完全不受影響。
     */
    publicSiteMode() {
      /*
          只看子站在後台的「隱藏小芋頭登入與服務選單」。

          子站網域與平台網域（taro.kf-9595.com/p/xxx）是同一個子站，header 必須一致。
          以前子站網域一律不顯示會員區，因為登入狀態綁網域、在那裡登入不了；
          現在登入改在平台網域做、再用交接碼帶回來（utils/custom-domain-auth.js），
          會員區的項目也都到平台網域開，所以兩邊都照後台設定走。

          平台自己的頁面沒有 navMenu，永遠是 false，行為完全不變。
      */
      return !!(this.navMenu && this.navMenu.hideTaroMemberArea);
    },

    enableNotifications() {
      return ENABLE_NOTIFICATIONS;
    },
    /** 通知未讀數 */
    notifyUnread() {
      return this.$store.state.notifications.unreadCount;
    },
    /** 下拉顯示最近數筆 */
    notifyItems() {
      return (this.$store.state.notifications.items || []).slice(0, 8);
    },
    /**顯示頭像 */
    avatarText() {
      const name = this.$store.state.userRealName?.trim();
      const email = this.userInfo.email?.trim();

      if (name) {
        return Array.from(name).slice(0, 2).join("");
      } else if (email) {
        return email.slice(0, 2).toUpperCase();
      }
      return "";
    },
  },
  methods: {
    /**
     * 把選單項目轉成要綁在 v-btn / v-list-item 上的屬性。
     *
     * 站內用 `to`（走 router，不重載整頁）；站外用 `href`（走 <a>，
     * 這樣才會被平台全域的外連攔截機制接到 —— 用 router.push 會繞過它）。
     * href 為 null 代表目標頁已下架，顯示成不可點而不是連到 404。
     */
    /**
     * 下拉母項要綁的屬性。
     *
     * 和一般項目差在「沒有連結目標時不能 disabled」——
     * navLinkProps 對沒有 href 的項目回 { disabled: true }，套在母項上會
     * 連下拉一起停用。母項底下的子頁可能好好的（例如母項自己還沒發佈），
     * 那樣整組選單就打不開了。
     */
    sourceIcon,
    sourceName,

    navParentProps(item) {
      const props = this.navLinkProps(item);
      return props.disabled ? {} : props;
    },

    /**
     * 這個主項是不是「只有標題、點了不轉跳」的那種：沒有連結但有子項。
     *
     * 它在視覺上與其他第一層完全相同（同一階就要對齊），
     * 唯一的差別是不掛連結屬性，所以點下去沒有反應。
     */
    isNavGroupOnly(item) {
      return !!item && !item.href && (item.children || []).length > 0;
    },

    hasNavChildren(item) {
      return !!item && (item.children || []).length > 0;
    },

    /** 手機抽屜裡這一組有沒有展開 */
    isDrawerGroupOpen(item) {
      return !!this.drawerOpen[item.key];
    },

    toggleDrawerGroup(item) {
      this.$set(this.drawerOpen, item.key, !this.drawerOpen[item.key]);
    },

    navLinkProps(item) {
      const href = (item && item.href) || "";

      if (!href) return { disabled: true };

      if (/^https?:\/\//i.test(href)) {
        const props = {
          href,
          target: item.openInNewTab ? "_blank" : "_self",
          rel: item.openInNewTab ? "noopener noreferrer" : undefined,
        };

        // 後台勾了「不顯示外連提醒」才加 data-no-guard，
        // 平台的外連攔截看到這個屬性會直接放行（external-link-guard.js:116）。
        //
        // 只在需要時才塞這個 key —— 那支是用 getAttribute() !== null 判斷，
        // 屬性存在就算數，給 "false" 也一樣會放行。
        if (item.skipExternalWarning) props["data-no-guard"] = "";

        return props;
      }

      // exact 是必要的：Vue Router 預設用「包含式」比對，
      // 所以首頁 /p/blackhorn 在 /p/blackhorn/booking 也會被判定為 active，
      // 結果是首頁那顆永遠亮著。改成精確比對，只有正在看的那一頁會亮。
      //
      // exactPath 也是必要的：exact 連網址參數一起比，從分享連結進來
      // （?refc=…、?share=…）時路徑明明相同，那一頁卻不會亮。只比路徑。
      return { to: href, exact: true, exactPath: true };
    },

    /**
     * 這個項目（或它底下的任一子項）是不是目前這一頁。
     *
     * 下拉的母項是 v-menu 的 activator、不是 router-link，
     * 拿不到 Vuetify 的 active class，所以自己判斷。
     */
    isNavItemActive(item) {
      if (!item) return false;

      const current = this.$route.path;
      const match = (x) => x && x.href && !/^https?:\/\//i.test(x.href) && x.href === current;

      // 往下找完整棵子樹：現在導覽有三層，只看直接子項的話，
      // 停在第三層時母項不會亮起來
      const matchDeep = (x) => match(x) || (x.children || []).some(matchDeep);

      return matchDeep(item);
    },

    // ===== 通知鈴鐺 =====
    onOpenNotify() {
      // 打開下拉時抓最近清單
      this.$store.dispatch("notifications/fetchList", { page: 1, pageSize: 8 });
    },
    onMarkAllRead() {
      this.$store.dispatch("notifications/markAllRead");
    },
    async onClickNotify(n) {
      const marking = this.$store.dispatch("notifications/markRead", n.id);
      this.notifyMenu = false;
      const route = resolveNotificationRoute(n.linkRoute, n.sourceSite);
      // 整頁離開前先等「已讀」送完，不然會被換頁中斷（見 utils/notification-nav）
      if (leavesPage(route)) await waitAtMost(marking);
      this.goByRoute(route);
    },
    goNotificationCenter() {
      this.notifyMenu = false;
      if (this.$route.path !== "/notifications") navigateMemberArea(this.$router, "/notifications");
    },
    goByRoute(route) {
      if (!route) return;
      if (/^https?:\/\//i.test(route)) {
        window.open(route, "_self");
      } else if (this.$route.fullPath !== route) {
        navigateMemberArea(this.$router, route);
      }
    },
    formatNotifyTime(t) {
      if (!t) return "";
      const d = new Date(t);
      if (isNaN(d.getTime())) return "";
      const now = new Date();
      const diff = (now - d) / 1000;
      if (diff < 60) return "剛剛";
      if (diff < 3600) return `${Math.floor(diff / 60)} 分鐘前`;
      if (diff < 86400) return `${Math.floor(diff / 3600)} 小時前`;
      const pad = (x) => String(x).padStart(2, "0");
      return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    isOn(v) {
      return v === "1" || v === "true" || v === "";
    },
    syncFromQuery(v) {
      const on = this.isOn(v);
      if (this.showShareOrg !== on) this.showShareOrg = on;
    },

    getUserGradeName_api(emailCode) {
      if (!emailCode || emailCode === "null" || emailCode === "undefined") return;
      Grade_GetUserGradeName(emailCode)
        .then((res) => {
          this.userGradeName = res.data;
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {});
    },
    /** 小芋頭服務清單。抓取與快取的邏輯在 utils/taro-services，四個 header 共用 */
    async loadTaroServices() {
      await fetchTaroServices({
        onServices: (services) => {
          this.taroService = services;
        },
        onLoading: (v) => {
          this.serviceLoading = v;
        },
      });
    },

    async logout() {
      // 手機的個人中心（MemberHome）也有登出鈕，邏輯放在 utils/signOut 共用
      await signOutAndReload(this);
    },

    prePage() {
      // 1. 優先處理顯式指定的 backRouter
      if (this.backRouter) {
        let target = this.backRouter;

        // 如果傳進來的是純字串 (例如 '/product')，先轉成物件格式以便合併 query
        if (typeof target === "string") {
          target = { path: target };
        }

        // 提取當前 query 中的關鍵參數
        const currentQuery = {};
        if (this.$route.query.refc) {
          currentQuery.refc = this.$route.query.refc;
        }
        if (this.$route.query.openExternalBrowser) {
          currentQuery.openExternalBrowser = this.$route.query.openExternalBrowser;
        }
        if (this.$route.query.category) {
          currentQuery.category = this.$route.query.category;
        }

        const finalLocation = {
          ...target,
          query: {
            ...currentQuery,
            ...(target.query || {}),
          },
        };

        this.$router.push(finalLocation).catch(() => {});
        return;
      }

      // 2. 如果沒指定 backRouter，則根據歷史紀錄決定行為
      if (this.hasHistory) {
        let from = this.$route.query.from;
        switch (from) {
          case "ai":
            window.location.replace(process.env.VUE_APP_MAIN_AICHAT_BASE_URL);
            break;

          default:
            window.history.back();
            break;
        }
      } else {
        // 3. 無歷史紀錄時，導向首頁並帶上原有參數
        const { refc, openExternalBrowser } = this.$route.query;
        const query = {};
        if (refc) query.refc = refc;
        if (openExternalBrowser) query.openExternalBrowser = openExternalBrowser;

        this.$router.push({ name: "Home", query }).catch(() => {});
      }
    },

    // 1. 核心邏輯：把當前的 query 參數附加到目標網址上
    appendCurrentQueryParams(targetUrl) {
      try {
        // 從當前路由取得參數，若網址上沒有，則嘗試從 Store (登入狀態) 取得
        const { refc: queryRefc, openExternalBrowser: queryOpen } = this.$route.query;
        const storeRefc = this.$store.state.username;

        // 優先順序：網址帶來的代碼 > 自己登入的代碼
        const refc = queryRefc || storeRefc;
        const openExternalBrowser = queryOpen || "1"; // 預設開啟外部瀏覽器開關

        // 準備要帶過去的參數物件
        const paramsToAdd = {};
        if (refc) paramsToAdd.refc = refc;
        if (openExternalBrowser) paramsToAdd.openExternalBrowser = openExternalBrowser;

        // 如果完全沒有參數可帶，就直接回傳原本的 url
        if (Object.keys(paramsToAdd).length === 0) {
          return targetUrl;
        }

        const isAbsolute = targetUrl.startsWith("http") || targetUrl.startsWith("//");
        // 子站網域上沒有平台頁面：相對路徑一律指到平台網域，並回傳完整網址
        const onCustomDomain = isCustomDomain();
        const baseUrl = onCustomDomain ? PLATFORM_BASE : window.location.origin;
        const urlObj = new URL(targetUrl, isAbsolute ? undefined : baseUrl);

        // 將參數加進去
        Object.keys(paramsToAdd).forEach((key) => {
          // 只有當目標網址原本沒有這個參數時才加入，避免重複或覆蓋目標原本的設定
          if (!urlObj.searchParams.has(key)) {
            urlObj.searchParams.set(key, paramsToAdd[key]);
          }
        });

        if (isAbsolute || onCustomDomain) {
          return urlObj.href;
        }
        return urlObj.pathname + urlObj.search + urlObj.hash;
      } catch (e) {
        console.error("URL append param error:", e);
        return targetUrl;
      }
    },
    // 前往會員中心
    goCenter(tab) {
      navigateMemberArea(this.$router, { name: "Center", query: { tab } });
    },
    GoRouter(url) {
      if (url === "SignIn" || url === "SignUp") {
        // 取得當前網址上的 refc（同名多值只取第一個）
        const raw = this.$route.query.refc;
        const refc = Array.isArray(raw) ? raw[0] : raw;

        // 子站網域：到平台登入，登入完帶著登入狀態回到這一頁（推薦碼一起帶過去）
        if (isCustomDomain()) {
          window.location.href = platformSignInUrl({ status: url, refc });
          return;
        }

        // 原地跳出登入／註冊對話框
        openLoginDialog({ refc, view: url });
        return;
      }

      if (url === "Product") {
        const { refc: queryRefc, openExternalBrowser } = this.$route.query;
        const refc = queryRefc || this.$store.state.username; // 新增：支援從 Store 抓取推薦碼

        const query = {};
        if (refc) query.refc = refc;
        if (openExternalBrowser) query.openExternalBrowser = openExternalBrowser;

        navigateMemberArea(this.$router, { name: "Product", query });
        return;
      }

      navigateMemberArea(this.$router, { name: url });
    },
    GoTaroService(item) {
      if (!item.action) return;
      const finalUrl = this.appendCurrentQueryParams(item.action);
      if (item.target === "_blank") {
        this.GoUrl(finalUrl);
      } else {
        leaveTo(finalUrl, item.name);
      }
    },
    GoUrl(url) {
      if (!url) return;

      // 1. 【關鍵修改】直接呼叫共用的工具函式
      // 無論傳入的是 https://google.com 還是 process.env.VUE_APP_XXX
      // 這個函式都會自動幫你判斷並補上 refc 與 openExternalBrowser
      const finalUrl = this.appendCurrentQueryParams(url);

      // 2. 開啟新視窗
      // 使用原本的邏輯，但加上簡單的安全性防護 (opener = null)
      let newWindow = window.open();

      if (newWindow) {
        newWindow.opener = null; // 安全性最佳實踐：防止新視窗透過 window.opener 操作原視窗
        newWindow.location.href = finalUrl;
      } else {
        // (選用) 如果 window.open 被瀏覽器攔截器擋住，newWindow 會是 null
        // 這裡可以選擇 fallback 到本頁轉跳，或是單純 console.log 提示
        console.warn("Popup blocked, redirecting in current tab.");
        window.location.href = finalUrl;
      }
    },

    /** 去預購 */
    goPreOrder(lv) {
      // 1. 先產生基礎目標網址
      let url = `${process.env.VUE_APP_MAIN_AICHAT_BASE_URL}/pre-order?lv=${lv}`;

      // 2. 使用 appendCurrentQueryParams 自動補上當前頁面的 query 參數 (refc, openExternalBrowser)
      let finalUrl = this.appendCurrentQueryParams(url);

      // 3. 開啟新視窗跳轉
      let newWindow = window.open();
      newWindow.location.href = finalUrl;
    },
    Share(url) {
      let preWord;
      let href;
      href = process.env.VUE_APP_MAIN_BASE_URL;

      if (url) {
        href += url;
      }

      if (url === "/ads") {
        preWord = `邀請您建立【小芋頭免費社群輪播廣告】網站 `;
      }

      this.$copyText(preWord + href)
        .then(() => {
          this.snackbar.show = true;
          this.snackbar.content = "連結已複製。";
        })
        .catch(() => {
          //console.log(err);
          this.snackbar.show = true;
          this.snackbar.content = "複製失敗，請手動複製。";
        });
      // App分享
      if (navigator.share && this.$store.state.userDevice.mobile) {
        navigator
          .share({
            title: "小芋頭廣告",
            text: preWord,
            url: href,
          })
          .then(() => {})
          .catch(() => {
            this.sheet = true;
          });
      }
    },
  },
};
</script>

<style>
.footer-h {
  height: 168px;
}

/* 通知鈴鐺下拉 */
.notify-item.notify-unread {
  background: #f3f0ff;
}
.notify-item:hover {
  background: #f5f5f5;
}
/*
  通知下拉的頭尾兩條 bar 要等高（「通知 / 全部已讀」與「查看全部」）。
  高度放在同一個變數裡，改一個地方兩邊一起動 ——
  分別寫死的話，改了上面忘了下面，看起來就是歪的。
*/
.notify-panel {
  --notify-bar-height: 56px;
  /* 窄螢幕讓步：固定 380px 在 360px 寬的手機上會頂到兩側邊緣 */
  max-width: min(380px, calc(100vw - 24px));
}

.notify-panel__bar {
  min-height: var(--notify-bar-height);
}

/* v-btn 的高度是內建樣式，要蓋掉才吃得到 */
.notify-panel__bar-action.v-btn {
  height: var(--notify-bar-height) !important;
}

/*
  來源站台圖示。比原本的 v-icon(18px) 大一點 —— 圖示本身有留白，
  和字級一樣大的話看起來反而比文字小。
  已讀不淡化，理由同通知中心。
*/
.notify-source-icon {
  width: 22px;
  height: 22px;
  object-fit: cover;
  flex-shrink: 0;
  /* 圖檔本身是方的，靠這裡裁成圓 —— 通知中心是用 v-avatar 裁的，
     兩邊要長一樣 */
  border-radius: 50%;
}

.notify-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #f44336;
  display: inline-block;
  flex-shrink: 0;
}

/* 通知文字那一欄；min-width:0 讓標題的 text-truncate 在旁邊有圖時仍然生效 */
.notify-item__main {
  flex: 1 1 auto;
  min-width: 0;
}

/* 自訂圖片固定方形縮圖，不管原圖比例，整列高度才不會被撐開 */
.notify-item__image {
  width: 56px;
  height: 56px;
  object-fit: cover;
  border-radius: 8px;
  flex-shrink: 0;
  background: #eeeeee;
}

/*
  子站的 header 黏在頂端（sticky prop）。

  不能像平台其他頁那樣把整個 Header2 包進 sticky 的 .fixed-bar：sticky 會自成
  一個疊層，裡面手機版導覽抽屜的 z-index 再高也出不去；抽屜的灰色遮罩卻是
  Vuetify 掛在頁面最外層、z-index 跟抽屜一樣 1000 —— 遮罩就蓋在抽屜上面，
  打開抽屜整片灰、點不到（2026-10-05 金豐企業大學）。

  所以只讓 app bar 本身 sticky，抽屜留在疊層外：
  - 外層 display: contents：外層高度只有一條 bar，sticky 在裡面沒地方黏；
    拿掉外層的框，app bar 直接以整頁為範圍
  - flex: 0 0 auto：自訂頁外框是直向 flex，app bar 變成它的直接子項，
    Vuetify 給 toolbar 的 flex: 1 1 auto 會把 bar 撐高
  - z-index 100 寫在 app bar 的 style 上
*/
.taro-header--sticky {
  display: contents;
}

.taro-header--sticky > .v-app-bar.v-toolbar {
  position: sticky;
  top: 0;
  flex: 0 0 auto;
}

/*
  服務選單的高度上限。

  選單用 fixed 定位黏在 header 下方（見 HeaderMenu.js），頁面捲動不會帶著它走 ——
  所以清單比畫面高時，底下幾項和「分享」按鈕要在選單裡面捲，否則永遠點不到。
  標題與分享按鈕固定，只有中間的服務清單捲動。

  96px = 選單上緣離畫面頂端的距離（header 內按鈕底 + nudge，約 60）+ 底部留白。
  不設的話 Vuetify 會把太高的選單往上推到離頂端 12px，蓋住 header。

  寫成 .v-card.taro-service-card 兩個 class：Vuetify 的 .v-card{display:block}
  會隨各頁的 CSS 重複載入，換頁後排在後面，單一 class 會輸掉 —— flex 失效，
  分享列溢出卡片（看起來底色透明），整個選單改用瀏覽器預設捲軸在捲。
*/
.v-card.taro-service-card {
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 96px);
  max-height: calc(100dvh - 96px); /* 手機網址列收合前，100vh 比實際可見高度長 */
  /* 選單寬 400，比多數手機畫面還寬；左右各留 12px，不要超出畫面 */
  max-width: calc(100vw - 24px);
}

.taro-service-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  /*
    v-row 自帶上下 -12px 負邊距。放進可捲動的容器，會多出 12px 可捲高度，
    清單明明放得下也冒出捲軸。row 的上下邊距歸零，再用這裡的 -4px
    抵回原本的位置（原本是容器 my-2 的 8px + row 的 -12px）。
  */
  margin: -4px 0;
}

.taro-service-scroll > .row {
  margin-top: 0;
  margin-bottom: 0;
}

.taro-service-scroll::-webkit-scrollbar {
  width: 6px;
}

.taro-service-scroll::-webkit-scrollbar-thumb {
  background: var(--v-primary-base, #6200ea86);
  border-radius: 6px;
}

.taro-service-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.service-block {
  padding: 0px;
  border-radius: 8px 0px 0px 0px;
  cursor: pointer;
}

.service-block:hover {
  background: var(--product-color-taro-gary-bg, #fafafa);
}

/* 自訂導覽（子站）—— 樣式比照 yentity 的 header */
/*
  LOGO 尺寸。

  以「高度」為準而不是寬度 —— header 的可用高度是固定的（64px），
  而 LOGO 的長寬比不固定：yentity 是長條形、黑牛角是正方形。
  寫死 width 會把正方形的 LOGO 拉成扁的或留大片空白。
  用 height + max-width + contain，長條與正方形都能自然擺放。
*/
/*
  高度對齊平台預設 LOGO 的既有尺寸（32px，見同檔 version 1 的寫法）。
  設成 40px 會把預設 LOGO 放大 25%，整條 header 看起來就變高 ——
  而選單沒設自訂 LOGO 時用的正是預設 LOGO，所以這是常見情況而非例外。
*/
.taro-nav__logo {
  height: 32px;
  width: auto;
  max-width: 140px;
  object-fit: contain;
  cursor: pointer;
}

/* 手機空間更小，LOGO 與最大寬度都收一點，才不會把右側會員區擠掉 */
@media (max-width: 600px) {
  .taro-nav__logo {
    height: 26px;
    max-width: 100px;
  }
}

.taro-nav {
  /* 項目多時可橫向捲，不要把右側的會員區擠掉或換行 */
  overflow-x: auto;
  scrollbar-width: none;
}

.taro-nav::-webkit-scrollbar {
  display: none;
}

/* 字體樣式：導覽按鈕與下拉／抽屜的文字共用 */
.taro-nav__item,
/*
  下拉裡的第三層。用縮排 + 前綴線表示從屬，不用縮小字級 ——
  導覽文字已經是 14px，再往下就低於 12px 的下限了。
*/
.taro-nav__sub {
  padding-left: 32px !important;
}

.taro-nav__text--sub {
  color: rgba(0, 0, 0, 0.72) !important;
}

.taro-nav__text {
  color: #000 !important;
  font-family: "Inter", sans-serif !important;
  font-size: 14px !important;
  font-style: normal !important;
  font-weight: 400 !important;
  line-height: normal !important;
  /* Vuetify 預設會把按鈕文字轉大寫並加字距，中文會變得很鬆 */
  text-transform: none !important;
  letter-spacing: 0 !important;
}

/*
  導覽按鈕。高度用 v-btn 預設值（與 yentity 一致）——
  先前為了塞進 56px 的 bar 壓成 32px，現在 bar 回到 68px 就不需要了。
  只收左右內距，讓項目排得緊一點。
*/
.taro-nav__item {
  flex-shrink: 0;
  min-width: 0 !important;
  padding: 0 12px !important;
  /* 未選取時淡一點，目前這一頁才對比得出來 */
  opacity: 0.6;
  transition: opacity 0.15s;
}

.taro-nav__item:hover {
  opacity: 1;
}

/*
  目前所在的頁面。

  v-btn--active 是 Vuetify 對 router-link 自動加的；
  taro-nav__item--active 是下拉母項自己加的（它是 v-menu 的 activator、
  不是 router-link，拿不到前者）。兩個都要寫。

  用 opacity + 底線而不是換顏色 —— 上面的基礎樣式寫了 color: #000 !important，
  在這裡改顏色要再疊一層 !important，會愈疊愈難維護。
*/
/* 目前這一頁：只用深淺區分，不加底線或其他裝飾 */
.taro-nav__item.v-btn--active,
.taro-nav__item--active {
  opacity: 1;
}

/* Vuetify 的 plain 按鈕自帶低透明度，會蓋掉上面的 opacity 控制 */
.taro-nav__item.v-btn--plain .v-btn__content {
  opacity: 1 !important;
}

/*
  ── 手機抽屜的層級 ───────────────────────────────────────────────

  三層攤平之後只靠縮排分不出主次（使用者實際回報看不出來）。
  縮排是很弱的訊號：中文字寬不一，第二層的長標題看起來反而比第一層的
  短標題「更靠左」。所以是三個訊號一起講同一件事 ——
  字級、字重、以及子項共用的那條導引線。

  顏色深淺**不**參與分層。試過拿它區分「不能點的母項」，被讀成
  「低一級」，反而把層級講錯了 —— 同一層就同一個樣子，只差點不點得動。

  字級下限 12px（子項 13、主項 15），沒有更小的。

  全部限定在 .taro-drawer 底下：Header2 還有十幾個既有頁面在用，
  它們的抽屜不該被一起改掉。
*/
.taro-drawer.v-list--nav {
  padding-left: 8px;
  padding-right: 8px;
}

/* ── 主項 ── */
.taro-drawer .taro-drawer__parent {
  min-height: 44px;
  margin-bottom: 2px !important;
  border-radius: 8px;
}

.taro-drawer .taro-drawer__parent-text {
  font-size: 15px !important;
  font-weight: 600 !important;
  color: #111 !important;
  line-height: 1.45 !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
}

/*
  目前這一頁：左側直條 + 淡底。

  刻意不用主題色 —— 這個抽屜是子站的品牌站在用，平台的紫色會跟他們的
  配色打架（桌機導覽也是同樣的理由，只用深淺區分）。
*/
.taro-drawer .taro-drawer__parent--active,
.taro-drawer .taro-drawer__parent.v-list-item--active {
  background: rgba(0, 0, 0, 0.055);
}

/*
  左邊那條直條，借 Vuetify 的 ::before 來畫。

  那個 ::before 本來是 hover／active 的整片遮罩（絕對定位撐滿四邊、
  背景 currentColor、border-radius: inherit），這裡把它改成靠左的 3px：
  右邊放掉、寬度給死、圓角歸零。

  為什麼不用 inset box-shadow：box-shadow 會跟著 border-radius 走，
  列本身有 8px 圓角，條的上下兩端就跟著彎，看起來是圓頭的膠囊
  而不是直條 —— 第一版就是這樣，被退回來過。

  也順便解決了另一件事 —— 那層遮罩用 currentColor，主題色是紫的，
  active 時會在列上疊一層紫。改成 #111 就沒有了。
*/
.taro-drawer .taro-drawer__parent--active::before,
.taro-drawer .taro-drawer__parent.v-list-item--active::before {
  opacity: 1 !important;
  right: auto;
  width: 3px;
  background-color: #111;
  border-radius: 0;
}

/*
  ── 不能點的主項 ──

  刻意「什麼都不改」：位置、字級、字重、顏色全部沿用 .taro-drawer__parent，
  因為它和其他第一層本來就是同一階。它點了是展開／收合子項，右邊有箭頭提示。

  之前是調淡顏色來表示「不能點」，結果被讀成「低一級」——
  層級只用縮排講，顏色不參與。
*/
.taro-drawer .taro-drawer__parent--group {
  cursor: pointer;
}

/* ── 一列：主項（佔滿）＋ 展開箭頭（有連結又有子項時才有）── */
.taro-drawer .taro-drawer__row {
  display: flex;
  align-items: center;
  margin-bottom: 2px;
}

.taro-drawer .taro-drawer__row > .taro-drawer__parent {
  flex: 1 1 auto;
  min-width: 0;
  margin-bottom: 0 !important;
}

/* 44px 見方：手指好按，也跟主項的高度一致 */
.taro-drawer .taro-drawer__toggle {
  flex: 0 0 auto;
  width: 44px !important;
  height: 44px !important;
  color: rgba(0, 0, 0, 0.6) !important;
}

.taro-drawer .taro-drawer__group-chevron {
  min-width: 0 !important;
}

.taro-drawer .taro-drawer__chevron {
  color: rgba(0, 0, 0, 0.6) !important;
  transition: transform 0.2s;
}

.taro-drawer .taro-drawer__chevron--open {
  transform: rotate(180deg);
}

@media (prefers-reduced-motion: reduce) {
  .taro-drawer .taro-drawer__chevron {
    transition: none;
  }
}

/* ── 子項 ── */
.taro-drawer .taro-drawer__children {
  margin: 2px 0 12px 18px;
  padding-left: 12px;
  border-left: 2px solid rgba(0, 0, 0, 0.1);
}

.taro-drawer .taro-drawer__child {
  min-height: 36px;
  margin-bottom: 0 !important;
  padding-left: 8px !important;
  border-radius: 6px;
}

.taro-drawer .taro-drawer__child-text {
  font-size: 13px !important;
  font-weight: 400 !important;
  color: rgba(0, 0, 0, 0.66) !important;
  line-height: 1.45 !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
}

/* 第三層再縮一階。只縮 14px —— 再多會讓長標題在 272px 寬裡換行 */
.taro-drawer .taro-drawer__child--deep {
  padding-left: 22px !important;
}

/*
  子項的目前這一頁只用淡底，不畫直條 —— 左邊已經有那條導引線了，
  再加一條會變成兩條平行線，分不出哪條在說什麼。

  這裡要關掉 Vuetify 的 active 遮罩：它用 currentColor，而 v-list--nav
  會把 active 的文字色設成主題色，於是列上會疊一層紫。
*/
.taro-drawer .taro-drawer__child.v-list-item--active {
  background: rgba(0, 0, 0, 0.045);
}

.taro-drawer .taro-drawer__child.v-list-item--active::before {
  opacity: 0 !important;
}

.taro-drawer .taro-drawer__child.v-list-item--active .taro-drawer__child-text {
  color: #111 !important;
  font-weight: 600 !important;
}

/*
  有自訂導覽時的 header 外觀，比照 YentityHeader：
  1px 淡灰分隔線、無陰影、左右內距桌機 40px / 手機 16px。

  全部限定在 .taro-nav__bar 底下 —— Header2 還有 10 幾個既有頁面在用，
  不加這個限定會把它們的 header 一起改掉。
*/
.taro-nav__bar {
  border-bottom: 1px solid #f0f0f0 !important;
  box-shadow: none !important;
}

/*
  這個 style 區塊沒有 scoped，所以直接寫後代選擇器就行 ——
  不要加 ::v-deep，它在非 scoped 樣式裡不是合法選擇器，會讓整條規則失效。
*/
.taro-nav__bar .v-toolbar__content {
  padding: 0 16px !important;
}

@media (min-width: 600px) {
  .taro-nav__bar .v-toolbar__content {
    padding: 0 40px !important;
  }
}

/* 服務 icon 骨架屏：清單/圖還沒到位時先佔住空間，避免版面跳動 */
.service-skeleton {
  background: #eeeeee;
  background-image: linear-gradient(90deg, #eeeeee 0%, #f5f5f5 40%, #eeeeee 80%);
  background-size: 200% 100%;
  animation: service-skeleton-shimmer 1.2s ease-in-out infinite;
}

.service-skeleton-icon {
  width: 38px;
  height: 38px;
}

.service-skeleton-text {
  width: 80%;
  height: 10px;
  border-radius: 4px;
}

@keyframes service-skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* 使用者若開了「減少動態效果」就不要閃 */
@media (prefers-reduced-motion: reduce) {
  .service-skeleton {
    animation: none;
  }
}

.info-menu-bg {
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  border-radius: 8px;
  border: 1px solid var(---line, #e0e0e0);
  background: linear-gradient(180deg, var(---Team-primary-Variants, #e3f2fd) 0%, #f5f5f5 100%), var(---Team-primary-Variants, #e3f2fd);
}

.linear-gradient-color {
  background: var(--Gradient, linear-gradient(90deg, #157af0 0%, #00bfa5 100%));
}

/* 頭像菜單圖示按鈕 - 確保不會爆版 */
.menu-icon-btn {
  flex: 1 1 0;
  min-width: 0 !important;
  max-width: 25%;
}

.menu-icon-btn .v-btn__content {
  white-space: nowrap;
}

/* 自訂捲軸樣式 */
.v-card::-webkit-scrollbar {
  width: 4px;
}

.v-card::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.15);
  border-radius: 4px;
}

.v-card::-webkit-scrollbar-track {
  background: transparent;
}

/* 針對極小螢幕 (400px 以下) 的特別處理 */
@media (max-width: 400px) {
  .menu-icon-btn {
    padding-left: 2px !important;
    padding-right: 2px !important;
  }

  .menu-icon-btn .text {
    font-size: 12px !important;
    letter-spacing: -0.5px;
    /* 緊縮字距 */
  }

  .menu-icon-btn .v-icon {
    font-size: 20px !important;
    margin-bottom: 2px !important;
  }
}
</style>
